/**
 * SEO Checklist — combined SEO score breakdown + readability checks for a product.
 * Renders coloured pills (green/amber/red) with tooltip explaining "why" and "how to fix".
 * Each non-good pill has an "Forbedre med AI" button that calls /api/seo/improve.
 * The "Autofiks alt" button at the top fixes ALL non-good aspects in one consolidated AI call
 * via /api/seo/improve-all/{product_id}.
 */
import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { CheckCircle, Warning, XCircle, ListChecks, Sparkle, FloppyDisk, MagicWand } from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const statusFromScore = (score, max) => {
  if (max === 0) return 'good';
  const pct = (score / max) * 100;
  if (pct >= 90) return 'good';
  if (pct >= 50) return 'improve';
  return 'bad';
};

const Pill = ({ status, label, tip, aspectId, onImprove }) => {
  const config = {
    good: {
      bg: 'bg-emerald-500/15 border-emerald-500/40 text-emerald-700 dark:text-emerald-400',
      Icon: CheckCircle,
    },
    improve: {
      bg: 'bg-amber-500/15 border-amber-500/40 text-amber-700 dark:text-amber-400',
      Icon: Warning,
    },
    bad: {
      bg: 'bg-red-500/15 border-red-500/40 text-red-700 dark:text-red-400',
      Icon: XCircle,
    },
  }[status] || {
    bg: 'bg-muted text-muted-foreground border-border',
    Icon: Warning,
  };
  return (
    <div className="inline-flex items-center gap-1">
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-medium ${config.bg}`}
            data-testid={`seo-pill-${status}-${label.toLowerCase().replace(/\s+/g, '-')}`}
          >
            <config.Icon size={12} weight="bold" />
            <span>{label}</span>
          </div>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs">
          <p className="text-xs">{tip}</p>
        </TooltipContent>
      </Tooltip>
      {status !== 'good' && aspectId && onImprove && (
        <button
          type="button"
          onClick={() => onImprove(aspectId, label)}
          className="text-[10px] uppercase tracking-wide text-muted-foreground hover:text-primary px-1 py-0.5 rounded transition-colors"
          data-testid={`improve-${aspectId}`}
          title={`Forbedre "${label}" med AI`}
        >
          <Sparkle size={11} weight="bold" className="inline" /> Fiks
        </button>
      )}
    </div>
  );
};

// Map SEO breakdown factor labels → aspect IDs the backend understands.
// Keys MUST exactly match the `factor` string returned by /api/seo/score-breakdown.
const SEO_ASPECT_MAP = {
  'Meta-tittel': 'meta_title',
  'Meta-beskrivelse': 'meta_description',
  'Kort beskrivelse': 'short_description',
  'Produktbeskrivelse': 'description',
  'Bilder + alt-tekst': null, // requires uploading images, no AI fix
  'Strukturert data': null,
  'Produktnavn (H1)': null,
  'URL/slug': null,
};

export default function SeoChecklist({ productId, onApplied }) {
  const [breakdown, setBreakdown] = useState(null);
  const [readability, setReadability] = useState(null);
  const [loading, setLoading] = useState(true);
  const [improving, setImproving] = useState(false);
  const [improvement, setImprovement] = useState(null); // { aspect, field, old, new, label }
  const [applying, setApplying] = useState(false);

  // Fix-all state
  const [fixingAll, setFixingAll] = useState(false);
  const [fixAllResults, setFixAllResults] = useState(null); // { results: { aspect: {field, old, new, ...} } }
  const [applyingAll, setApplyingAll] = useState(false);

  const getHeaders = useCallback(
    () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` }),
    []
  );

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [seo, read] = await Promise.all([
        axios.get(`${API}/api/seo/score-breakdown/${productId}`, { headers: getHeaders() }),
        axios.get(`${API}/api/seo/readability/${productId}`, { headers: getHeaders() }),
      ]);
      setBreakdown(seo.data);
      setReadability(read.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kunne ikke laste sjekkliste');
    } finally {
      setLoading(false);
    }
  }, [productId, getHeaders]);

  useEffect(() => {
    reload();
  }, [reload]);

  const handleImprove = async (aspectId, label) => {
    if (!aspectId) {
      toast.info(`"${label}" må fikses manuelt — ingen AI-løsning tilgjengelig.`);
      return;
    }
    setImproving(true);
    try {
      const { data } = await axios.post(
        `${API}/api/seo/improve/${productId}`,
        { aspect: aspectId },
        { headers: getHeaders() }
      );
      setImprovement({ ...data, label });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'AI-forslag feilet');
    } finally {
      setImproving(false);
    }
  };

  const applyImprovement = async () => {
    if (!improvement) return;
    setApplying(true);
    try {
      // Save to product via PUT /api/products/{id} (only updates the field we changed)
      await axios.put(
        `${API}/api/products/${productId}`,
        { [improvement.field]: improvement.new },
        { headers: getHeaders() }
      );
      toast.success(`"${improvement.label}" oppdatert`);
      setImprovement(null);
      await reload();
      if (onApplied) onApplied();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kunne ikke lagre');
    } finally {
      setApplying(false);
    }
  };

  // ---- Autofiks alt (Fix all non-good aspects in one call) ----
  const collectNonGoodAspects = useCallback(() => {
    const ids = new Set();
    (breakdown?.breakdown || []).forEach((b) => {
      const status = statusFromScore(b.score, b.max);
      if (status === 'good') return;
      const aspectId = SEO_ASPECT_MAP[b.factor];
      if (aspectId) ids.add(aspectId);
    });
    (readability?.checks || []).forEach((c) => {
      if (c.status !== 'good' && c.id) ids.add(c.id);
    });
    return Array.from(ids);
  }, [breakdown, readability]);

  const handleFixAll = async () => {
    const aspects = collectNonGoodAspects();
    if (aspects.length === 0) {
      toast.success('Alt er allerede optimalt!');
      return;
    }
    setFixingAll(true);
    try {
      const { data } = await axios.post(
        `${API}/api/seo/improve-all/${productId}`,
        { aspects },
        { headers: getHeaders(), timeout: 120000 }
      );
      setFixAllResults(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Autofiks feilet');
    } finally {
      setFixingAll(false);
    }
  };

  const applyFixAll = async () => {
    if (!fixAllResults?.results) return;
    setApplyingAll(true);
    try {
      // Build a single PUT payload with all field updates that succeeded.
      const payload = {};
      let okCount = 0;
      let errCount = 0;
      Object.values(fixAllResults.results).forEach((r) => {
        if (r.error || !r.field || r.new == null) {
          errCount += 1;
          return;
        }
        payload[r.field] = r.new;
        okCount += 1;
      });
      if (okCount === 0) {
        toast.error('Ingen forbedringer å lagre');
        return;
      }
      await axios.put(`${API}/api/products/${productId}`, payload, {
        headers: getHeaders(),
      });
      toast.success(
        `${okCount} felt oppdatert${errCount ? ` (${errCount} feilet)` : ''}`
      );
      setFixAllResults(null);
      await reload();
      if (onApplied) onApplied();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Kunne ikke lagre samlet');
    } finally {
      setApplyingAll(false);
    }
  };

  if (loading) {
    return <Card><CardContent className="p-4 text-sm text-muted-foreground">Laster sjekkliste...</CardContent></Card>;
  }
  if (!breakdown) return null;

  const seoChecks = (breakdown.breakdown || []).map((b) => ({
    status: statusFromScore(b.score, b.max),
    label: b.factor,
    tip: `${b.score}/${b.max} poeng — ${b.tip}`,
    aspectId: SEO_ASPECT_MAP[b.factor],
  }));
  const readabilityChecks = (readability?.checks || []).map((c) => ({
    status: c.status,
    label: c.label,
    tip: c.tip,
    aspectId: c.id,
  }));

  const seoGood = seoChecks.filter((c) => c.status === 'good').length;
  const readGood = readabilityChecks.filter((c) => c.status === 'good').length;
  const fixableCount = collectNonGoodAspects().length;

  return (
    <TooltipProvider delayDuration={200}>
      <Card className="border border-border" data-testid="seo-checklist-card">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2 flex-wrap">
            <CardTitle className="font-heading text-base flex items-center gap-2 mr-auto">
              <ListChecks size={18} weight="bold" />
              SEO-sjekkliste
            </CardTitle>
            <span className="text-xs font-normal text-muted-foreground hidden sm:inline">
              Hold over for forklaring
            </span>
            <Button
              size="sm"
              variant="default"
              className="gap-2"
              onClick={handleFixAll}
              disabled={fixingAll || fixableCount === 0}
              data-testid="autofix-all-btn"
              title={
                fixableCount === 0
                  ? 'Alt er allerede optimalt'
                  : `Fiks alle ${fixableCount} ikke-grønne aspekter med AI`
              }
            >
              <MagicWand size={14} weight="bold" />
              {fixingAll
                ? 'Genererer...'
                : `Autofiks alt${fixableCount ? ` (${fixableCount})` : ''}`}
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-5">
          {/* SEO score panel */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1">
                <Sparkle size={12} weight="bold" /> SEO-skåre {breakdown.score}/100
              </p>
              <span className="text-xs text-muted-foreground" data-testid="seo-good-count">
                {seoGood} av {seoChecks.length} optimale
              </span>
            </div>
            <div className="flex flex-wrap gap-2" data-testid="seo-pills">
              {seoChecks.map((c) => (
                <Pill key={`seo-${c.label}`} {...c} onImprove={handleImprove} />
              ))}
            </div>
          </div>

          {/* Readability panel */}
          {readability && (
            <div className="pt-4 border-t border-border/60">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1">
                  <Sparkle size={12} weight="bold" /> Lesbarhet {readability.score}/100
                </p>
                <span className="text-xs text-muted-foreground" data-testid="readability-good-count">
                  {readGood} av {readabilityChecks.length} optimale
                </span>
              </div>
              <div className="flex flex-wrap gap-2" data-testid="readability-pills">
                {readabilityChecks.map((c) => (
                  <Pill key={`r-${c.aspectId || c.label}`} {...c} onImprove={handleImprove} />
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* AI improvement preview modal (single aspect) */}
      <Dialog open={!!improvement || improving} onOpenChange={(open) => !open && !applying && setImprovement(null)}>
        <DialogContent className="max-w-3xl" data-testid="improvement-dialog">
          <DialogHeader>
            <DialogTitle>
              {improving ? 'AI genererer forbedring...' : `Forbedring: ${improvement?.label || ''}`}
            </DialogTitle>
          </DialogHeader>
          {improving ? (
            <div className="py-12 flex items-center justify-center text-muted-foreground">
              <Sparkle size={20} weight="bold" className="animate-pulse mr-2" />
              <span className="text-sm">Henter AI-forslag...</span>
            </div>
          ) : improvement && (
            <div className="space-y-3">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">Eksisterende</p>
                <div
                  className="border border-border rounded p-3 bg-muted/30 text-sm max-h-48 overflow-y-auto prose prose-sm max-w-none"
                  dangerouslySetInnerHTML={{ __html: improvement.old || '<em class="text-muted-foreground">Tom</em>' }}
                  data-testid="improvement-old"
                />
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700 dark:text-emerald-400 mb-1">Foreslått ny</p>
                <div
                  className="border border-emerald-500/40 rounded p-3 bg-emerald-500/5 text-sm max-h-64 overflow-y-auto prose prose-sm max-w-none"
                  dangerouslySetInnerHTML={{ __html: improvement.new }}
                  data-testid="improvement-new"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setImprovement(null)} disabled={applying}>
              Avbryt
            </Button>
            <Button onClick={applyImprovement} disabled={applying || !improvement} className="gap-2" data-testid="apply-improvement-btn">
              <FloppyDisk size={14} weight="bold" />
              {applying ? 'Lagrer...' : 'Bruk forslag'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Fix-all preview modal */}
      <Dialog
        open={!!fixAllResults || fixingAll}
        onOpenChange={(open) => !open && !applyingAll && setFixAllResults(null)}
      >
        <DialogContent className="max-w-4xl" data-testid="fixall-dialog">
          <DialogHeader>
            <DialogTitle>
              {fixingAll
                ? 'AI fikser alle aspekter...'
                : `Forhåndsvisning av AI-forbedringer${fixAllResults?.results
                    ? ` (${Object.keys(fixAllResults.results).length})`
                    : ''}`}
            </DialogTitle>
          </DialogHeader>
          {fixingAll ? (
            <div className="py-16 flex flex-col items-center justify-center text-muted-foreground">
              <MagicWand size={28} weight="bold" className="animate-pulse mb-3" />
              <span className="text-sm">Dette kan ta opptil 60 sekunder...</span>
            </div>
          ) : fixAllResults?.results && (
            <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-1">
              {Object.entries(fixAllResults.results).map(([aspect, r]) => (
                <div key={aspect} className="border border-border rounded p-3" data-testid={`fixall-row-${aspect}`}>
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-semibold uppercase tracking-wide">
                      {r.field || aspect}
                    </p>
                    {r.error ? (
                      <span className="text-xs text-red-600">Feilet</span>
                    ) : (
                      <span className="text-xs text-emerald-600">Klar</span>
                    )}
                  </div>
                  {r.error ? (
                    <p className="text-xs text-red-600">{r.error}</p>
                  ) : (
                    <div className="grid md:grid-cols-2 gap-2">
                      <div>
                        <p className="text-[10px] uppercase text-muted-foreground mb-1">Før</p>
                        <div
                          className="border border-border rounded p-2 bg-muted/30 text-xs max-h-40 overflow-y-auto prose prose-xs max-w-none"
                          dangerouslySetInnerHTML={{ __html: r.old || '<em class="text-muted-foreground">Tom</em>' }}
                        />
                      </div>
                      <div>
                        <p className="text-[10px] uppercase text-emerald-700 dark:text-emerald-400 mb-1">Ny</p>
                        <div
                          className="border border-emerald-500/40 rounded p-2 bg-emerald-500/5 text-xs max-h-40 overflow-y-auto prose prose-xs max-w-none"
                          dangerouslySetInnerHTML={{ __html: r.new || '' }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setFixAllResults(null)} disabled={applyingAll}>
              Avbryt
            </Button>
            <Button
              onClick={applyFixAll}
              disabled={applyingAll || !fixAllResults?.results}
              className="gap-2"
              data-testid="apply-fixall-btn"
            >
              <FloppyDisk size={14} weight="bold" />
              {applyingAll ? 'Lagrer...' : 'Bruk alle'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </TooltipProvider>
  );
}
